/**
 * Cache UI das liberações do paciente.
 * Ao abrir: GET individual MedicationDispense (STATISTICS) e hidrata o Map.
 * @module purchase-release/useLocalPurchaseReleases
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CancelMedicationDispense } from "@/services/fhir/patientsummary/medicationdispense/CancelMedicationDispense";
import { GetMedicationDispenseByPatient } from "@/services/fhir/patientsummary/medicationdispense/GetMedicationDispenseByPatient";

import { toPurchaseReleaseListItems } from "./listHelpers";
import {
  groupDispensesIntoBatches,
  purchaseReleaseMemory,
  setPatientPurchaseReleaseBatches,
  subscribePurchaseReleaseMemory,
} from "./releaseMemory";
import type { LocalPurchaseReleaseBatch, PurchaseReleaseListItem } from "./types";

export interface UseLocalPurchaseReleasesOptions {
  organizationId?: string;
  storeName?: string;
  enabled?: boolean;
}

export function useLocalPurchaseReleases(
  patientKey: string | undefined,
  options?: UseLocalPurchaseReleasesOptions,
) {
  const key = patientKey?.trim() || "";
  const organizationId = options?.organizationId?.trim() || "";
  const storeName = options?.storeName || "";
  const enabled = options?.enabled !== false;

  const [batches, setBatches] = useState<LocalPurchaseReleaseBatch[]>(() =>
    key ? [...(purchaseReleaseMemory.get(key) ?? [])] : [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setBatches(key ? [...(purchaseReleaseMemory.get(key) ?? [])] : []);
  }, [key]);

  useEffect(() => {
    return subscribePurchaseReleaseMemory(() => {
      setBatches(key ? [...(purchaseReleaseMemory.get(key) ?? [])] : []);
    });
  }, [key]);

  /**
   * GET individual STATISTICS — batch de dispense ainda instável no servidor.
   * Merge: servidor vence por batchId; mantém lotes locais sem id FHIR.
   */
  const refetch = useCallback(async () => {
    if (!key || !organizationId || !enabled) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);

    try {
      const dispenses = await GetMedicationDispenseByPatient({
        patientReference: key,
        organizationId,
      });
      if (requestId !== requestIdRef.current) return;

      const fromServer = groupDispensesIntoBatches(
        dispenses,
        key,
        organizationId,
        storeName,
      );
      const existing = purchaseReleaseMemory.get(key) ?? [];
      const byId = new Map(fromServer.map((b) => [b.batchId, b]));
      // Mantém lotes locais ainda não refletidos no GET (ex.: POST acabou de gravar)
      for (const local of existing) {
        if (!byId.has(local.batchId)) {
          byId.set(local.batchId, local);
        }
      }

      const next = [...byId.values()];
      setPatientPurchaseReleaseBatches(key, next);
      setBatches(next);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [key, organizationId, storeName, enabled]);

  useEffect(() => {
    void refetch();
    return () => {
      requestIdRef.current += 1;
    };
  }, [refetch]);

  const addBatch = useCallback(
    (batch: LocalPurchaseReleaseBatch) => {
      if (!key) return;
      setBatches((prev) => {
        const next = [batch, ...prev];
        setPatientPurchaseReleaseBatches(key, next);
        return next;
      });
    },
    [key],
  );

  const revokeBatch = useCallback(
    async (batchId: string) => {
      if (!key) return;

      const current = purchaseReleaseMemory.get(key) ?? [];
      const target = current.find((batch) => batch.batchId === batchId);
      if (!target) return;

      const ids = target.dispenses
        .map((d) => d.id?.trim())
        .filter((id): id is string => Boolean(id));

      if (ids.length) {
        await Promise.all(ids.map((id) => CancelMedicationDispense(id)));
      }

      setBatches((prev) => {
        const next = prev.filter((batch) => batch.batchId !== batchId);
        setPatientPurchaseReleaseBatches(key, next);
        return next;
      });
    },
    [key],
  );

  const listItems: PurchaseReleaseListItem[] = useMemo(
    () => toPurchaseReleaseListItems(batches),
    [batches],
  );

  return {
    batches,
    listItems,
    addBatch,
    revokeBatch,
    refetch,
    loading: loading && listItems.length === 0,
    refreshing: loading && listItems.length > 0,
    error,
    hasReleases: listItems.length > 0,
  };
}
